import { fetchToken, saveToken } from '../../db/auth';
import { refreshAccessToken } from './game-session';
import { GAME_DB } from '../../db/game';
import { makeOpenApiRequest, makePrivateApiRequest } from './utils';
import { getCurrentLangId } from '../utils';
import { GAME_END_TIME, GAME_START_TIME, GAME_TIME_LEFT } from '../../app.config.vars/stored';
import { API_ERRORS } from '../../app.config.vars/errorCodes';
import { store } from '../../store';
import { setIsModalOpen, setModalContent } from '../../store/slices/global';
import { MODAL_CONTENT } from '../../app.config.vars/modalContent';
const destroyGame = async () => {
    await saveToken('accessToken', '');
    await saveToken('refreshToken', '');
    await GAME_DB.RESET.game();
    localStorage.setItem(GAME_START_TIME, '');
    localStorage.setItem(GAME_END_TIME, '');
    localStorage.setItem(GAME_TIME_LEFT, '');
};
export const apiUrl = '/_api';
export const generateRequestUrl = (endpoint) => `${location.origin}${apiUrl}${endpoint}`;
export const generateQueryParamsString = (params) => Object.entries(params)
    .filter(([, value]) => !!value)
    .map(([key, value], index) => (index === 0 ? `?${key}=${value}` : `&${key}=${value}`))
    .join('');
export const OPEN_API = {
    GET: {
        all: async ({ endpoint, queryParams }) => {
            const queryString = queryParams ? generateQueryParamsString(queryParams) : '';
            const url = generateRequestUrl(endpoint + queryString);
            const { response, data } = await makeOpenApiRequest(url, 'GET', undefined, getCurrentLangId());
            return response.ok ? data['hydra:member'] : false;
        },
        single: async ({ endpoint, id }) => {
            const url = generateRequestUrl(`${endpoint}/${id}`);
            const { response, data } = await makeOpenApiRequest(url, 'GET', undefined, getCurrentLangId());
            return response.ok ? data : false;
        },
    },
    POST: async ({ endpoint, body }) => {
        const url = generateRequestUrl(endpoint);
        const { response, data } = await makeOpenApiRequest(url, 'POST', body, getCurrentLangId());
        return response.ok ? data : false;
    },
};
let refreshTokenPromise = null;
const ensureFreshAccessToken = async () => {
    if (refreshTokenPromise) {
        await refreshTokenPromise;
        return fetchToken('accessToken');
    }
    const handleRefreshFailed = async () => {
        await destroyGame();
        store.dispatch(setIsModalOpen(true));
        store.dispatch(setModalContent(MODAL_CONTENT.cannotFinishGame.key));
    };
    refreshTokenPromise = (async () => {
        const refreshToken = await fetchToken('refreshToken');
        const tokens = await refreshAccessToken(refreshToken);
        if (tokens) {
            await saveToken('accessToken', tokens.jwt);
            await saveToken('refreshToken', tokens.refreshToken);
        }
        else {
            const activeGame = await GAME_DB.GET.game();
            if (activeGame) {
                await handleRefreshFailed();
            }
        }
    })();
    try {
        await refreshTokenPromise;
    }
    catch {
        refreshTokenPromise = null;
        throw new Error('Failed to refresh token');
    }
    refreshTokenPromise = null;
    return fetchToken('accessToken');
};
export const PRIVATE_API = {
    GET: {
        all: async ({ endpoint, queryParams }) => {
            const queryString = queryParams ? generateQueryParamsString(queryParams) : '';
            const url = generateRequestUrl(endpoint + queryString);
            let token = await fetchToken('accessToken');
            try {
                let { response, data } = await makePrivateApiRequest(url, 'GET', token, undefined, getCurrentLangId());
                if (response.ok) {
                    return { data: data['hydra:member'] };
                }
                if (data.error?.code === API_ERRORS.tokenExpired) {
                    token = await ensureFreshAccessToken();
                    const retry = await makePrivateApiRequest(url, 'GET', token, undefined, getCurrentLangId());
                    return retry.response.ok
                        ? { data: retry.data['hydra:member'] }
                        : { error: { code: retry.data.error.code, message: retry.data.error.message } };
                }
                if (data.error?.code === API_ERRORS.invalidGameSession) {
                    await destroyGame();
                    store.dispatch(setIsModalOpen(true));
                    store.dispatch(setModalContent(MODAL_CONTENT.cannotFinishGame.key));
                }
                return { error: { code: data.error.code, message: data.error.message } };
            }
            catch {
                return { error: { code: 'no_internet', message: 'No internet connection' } };
            }
        },
        single: async ({ endpoint, queryParams }) => {
            const queryString = queryParams ? generateQueryParamsString(queryParams) : '';
            const url = generateRequestUrl(endpoint + queryString);
            let token = await fetchToken('accessToken');
            try {
                let { response, data } = await makePrivateApiRequest(url, 'GET', token, undefined, getCurrentLangId());
                if (response.ok) {
                    return { data };
                }
                if (data.error?.code === API_ERRORS.tokenExpired) {
                    token = await ensureFreshAccessToken();
                    const retry = await makePrivateApiRequest(url, 'GET', token, undefined, getCurrentLangId());
                    return retry.response.ok
                        ? { data: retry.data }
                        : { error: { code: retry.data.error.code, message: retry.data.error.message } };
                }
                if (data.error?.code === API_ERRORS.invalidGameSession) {
                    await destroyGame();
                    store.dispatch(setIsModalOpen(true));
                    store.dispatch(setModalContent(MODAL_CONTENT.cannotFinishGame.key));
                }
                return { error: { code: data.error.code, message: data.error.message } };
            }
            catch {
                return { error: { code: 'no_internet', message: 'No internet connection' } };
            }
        },
    },
    POST: async ({ endpoint, body }) => {
        const url = generateRequestUrl(endpoint);
        let token = await fetchToken('accessToken');
        try {
            let { response, data } = await makePrivateApiRequest(url, 'POST', token, body, getCurrentLangId());
            if (response.ok) {
                return { data };
            }
            if (data.error?.code === API_ERRORS.tokenExpired) {
                token = await ensureFreshAccessToken();
                const retry = await makePrivateApiRequest(url, 'POST', token, body, getCurrentLangId());
                return retry.response.ok
                    ? { data: retry.data }
                    : { error: { code: retry.data.error.code, message: retry.data.error.message } };
            }
            if (data.error?.code === API_ERRORS.invalidGameSession) {
                await destroyGame();
                store.dispatch(setIsModalOpen(true));
                store.dispatch(setModalContent(MODAL_CONTENT.cannotFinishGame.key));
            }
            return { error: { code: data.error.code, message: data.error.message } };
        }
        catch {
            return { error: { code: 'no_internet', message: 'No internet connection' } };
        }
    },
};
